/**
 * Axios 인스턴스 설정
 * - 학생용 API 클라이언트
 * - 인증 토큰 자동 추가
 * - 401 에러 시 자동 로그아웃
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, clearAuthData } from '../utils/storage';

/**
 * Axios 인스턴스 생성
 */
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 요청 인터셉터
 * - Authorization 헤더 자동 추가
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();

    // 토큰이 있으면 헤더에 추가
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 응답 인터셉터
 * - 401 에러 시 자동 로그아웃
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // 401 Unauthorized: 로그아웃 처리
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const currentPath = window.location.pathname;

      // 로그인 엔드포인트 요청이거나 이미 로그인 페이지에 있으면 리다이렉트하지 않음
      const isLoginRequest = requestUrl.includes('/auth/login');
      const isLoginPage = currentPath === '/login';

      if (!isLoginRequest && !isLoginPage) {
        // 로그인이 아닌 다른 요청에서 401이 발생한 경우에만 로그아웃 처리
        clearAuthData();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
